import axios from "axios";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Variable, envVariable } from "@/common/constants/envVariable";
import { Controller, Endpoint, mapUrl } from "@/repository";
import { authentication as microsoftAuth } from "@microsoft/teams-js";
import { ResponseStatus } from "@/repository/models";
import { isTokenExpired } from "@/common/authentication/jwtDecoder";
import { embededInTeams } from "./authenticationUtils";
import { authenticateMsal, msalConfig } from "./msalConfig";
import { captureError } from "../helpers/sentryHelper";

export interface RefreshTokenError {
	message?: string;
	data?: any;
}

export class NetworkError extends Error {
	constructor(message = "Network error") {
		super(message);
		this.name = "NetworkError";
	}
}

export class TokenManager {
	public async getTokenOrRefresh(): Promise<string> {
		const token = Lockr.get(StorageKeys.TokenId);

		if (!isTokenExpired(token)) {
			return token;
		}

		return this.tryRefreshToken();
	}

	public async tryRefreshToken(): Promise<string> {
		const token = await this.getSSOTokenForSilentRefreshTokenAcquire();

		if (token == null) {
			throw { message: "MS token is null" } as RefreshTokenError;
		}

		return this.refreshTokenInternal(token);
	}

	private async refreshTokenInternal(
		msalToken: string		
	): Promise<string> {
		try {
			const body = this.buildRequestBody(msalToken);
			const config = this.buildRequestConfig();
			const response = await axios.post(this.fullUrlRequest(), body, config);
			const data = response.data.data;

			if (response.data.result !== ResponseStatus.SUCCESS) {
				const error = new Error(
					"Token refresh failed - unsuccessful response"
				) as RefreshTokenError & Error;
				error.data = data;

				throw error;
			}

			Lockr.set(StorageKeys.TokenId, data);
			return data;
		} catch (error: any) {
			if (error.response) {
				const err = new Error(
					`Token refresh failed with status ${error.response.status}`
				) as RefreshTokenError & Error;
				err.data = error.response.data;

				throw err;
			} else if (error.request) {
				throw new NetworkError("Token refresh failed - network error");
			}
			
			throw error;
		}
	}

	private async getSSOTokenForSilentRefreshTokenAcquire(): Promise<
		string | null
	> {
		try {
			if (embededInTeams) {
				return await microsoftAuth.getAuthToken();
			} else {
				const result = await authenticateMsal({
					scopes: [`${msalConfig.auth.clientId}/.default`],
				});

				return result?.idToken ?? null;
			}
		} catch (error: any) {
			captureError(error, "Failed to acquire MS token");

			return null;
		}
	}

	private readonly buildRequestBody = (token: string) => ({
		msalJwt: token,		
		appClientJwt: Lockr.get(StorageKeys.TokenId),
	});

	private readonly buildRequestConfig = () => ({
		headers: { __tenant: Lockr.get(StorageKeys.TenantId) || "" },
		withCredentials: true,
	});

	private readonly fullUrlRequest = (): string =>
		`${envVariable(Variable.GatewayApiUrl)}/${mapUrl(
			Controller.Account,
			Endpoint.Refresh
		)}`;
}
